import React from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { useLang } from '../i18n/LanguageContext';
import { CONTROLLER_SPECS, SPEC_LABELS, MARKING_IMAGE, BRACELET_SPEC } from '../data/controllerSpecs';

export const ControllerSheetModal = ({ controllerKey, title, description, onClose }) => {
  const { lang } = useLang();
  const L = SPEC_LABELS[lang] || SPEC_LABELS.fr;
  const spec = CONTROLLER_SPECS[controllerKey];
  if (!spec) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-[#0a0a0a] border border-white/10 p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="controller-sheet-modal">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{L.sheet}</DialogDescription>
        <button onClick={onClose} aria-label={L.close} className="absolute right-4 top-4 z-10 text-gray-400 hover:text-white transition-colors" data-testid="controller-sheet-close">
          <X className="h-5 w-5" />
        </button>

        <div className="grid sm:grid-cols-2">
          <div className="bg-black/60 p-6 flex items-center justify-center">
            <img loading="lazy" src={spec.image} alt={title} className="w-full max-w-[240px] object-contain" />
          </div>
          <div className="p-6">
            <p className="text-purple-400 text-xs uppercase tracking-[0.2em] mb-2">{L.sheet} · {spec.ref}</p>
            <h2 className="text-white text-xl font-bold mb-2">{title}</h2>
            <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.specs}</h3>
            <dl className="divide-y divide-white/5" data-testid="controller-sheet-specs">
              {spec.rows.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-4 py-2">
                  <dt className="text-gray-400 text-sm">{L[key]}</dt>
                  <dd className="text-white text-sm font-medium text-right">{value}</dd>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 text-sm shrink-0">{L.control}</dt>
                <dd className="text-white text-sm font-medium text-right">{spec.control[lang] || spec.control.fr}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.functions}</h3>
            <ul className="space-y-2">
              {(spec.functions[lang] || spec.functions.fr).map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-300 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const BraceletSheetModal = ({ image, name, onClose }) => {
  const { lang } = useLang();
  const L = SPEC_LABELS[lang] || SPEC_LABELS.fr;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-[#0a0a0a] border border-white/10 p-0 rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="bracelet-sheet-modal">
        <DialogTitle className="sr-only">{L.braceletSheet}</DialogTitle>
        <DialogDescription className="sr-only">{name}</DialogDescription>
        <button onClick={onClose} aria-label={L.close} className="absolute right-4 top-4 z-10 text-gray-400 hover:text-white transition-colors" data-testid="bracelet-sheet-close">
          <X className="h-5 w-5" />
        </button>

        <div className="grid sm:grid-cols-2">
          <div className="bg-black/60 p-6 flex items-center justify-center">
            <img loading="lazy" src={image} alt={name} className="w-full max-w-[260px] object-contain rounded-lg" />
          </div>
          <div className="p-6">
            <p className="text-purple-400 text-xs uppercase tracking-[0.2em] mb-2">{L.sheet} · {BRACELET_SPEC.ref}</p>
            <h2 className="text-white text-xl font-bold mb-2">{L.braceletSheet}</h2>
            <p className="text-gray-400 text-sm leading-relaxed">{name}</p>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.specs}</h3>
            <dl className="divide-y divide-white/5" data-testid="bracelet-sheet-specs">
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 text-sm shrink-0">{L.material}</dt>
                <dd className="text-white text-sm font-medium text-right">{BRACELET_SPEC.material[lang] || BRACELET_SPEC.material.fr}</dd>
              </div>
              {BRACELET_SPEC.rows.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between gap-4 py-2">
                  <dt className="text-gray-400 text-sm">{L[key]}</dt>
                  <dd className="text-white text-sm font-medium text-right">{value}</dd>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 py-2">
                <dt className="text-gray-400 text-sm shrink-0">{L.water}</dt>
                <dd className="text-white text-sm font-medium text-right">{BRACELET_SPEC.water[lang] || BRACELET_SPEC.water.fr}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 py-2">
                <dt className="text-gray-400 text-sm">{L.nfc}</dt>
                <dd className="text-white text-sm font-medium text-right">✓</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-3">{L.functions}</h3>
            <ul className="space-y-2">
              {(BRACELET_SPEC.functions[lang] || BRACELET_SPEC.functions.fr).map((f) => (
                <li key={f} className="flex items-start gap-2 text-gray-300 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ControllerPickerModal = ({ controllers, onPick, onClose }) => {
  const { lang } = useLang();
  const L = SPEC_LABELS[lang] || SPEC_LABELS.fr;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-[#0a0a0a] border border-white/10 p-0 rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="controller-picker-modal">
        <DialogTitle className="sr-only">{L.pickController}</DialogTitle>
        <DialogDescription className="sr-only">{L.pickControllerSub}</DialogDescription>
        <button onClick={onClose} aria-label={L.close} className="absolute right-4 top-4 z-10 text-gray-400 hover:text-white transition-colors" data-testid="controller-picker-close">
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          <p className="text-purple-400 text-xs uppercase tracking-[0.2em] mb-2">{L.pickControllerSub}</p>
          <h2 className="text-white text-xl font-bold mb-6">{L.pickController}</h2>

          <div className="grid sm:grid-cols-3 gap-4">
            {controllers.map((c) => (
              <button
                key={c.key}
                onClick={() => onPick(c)}
                className="text-left bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/60 transition-colors group"
                data-testid={`controller-picker-${c.key}`}
              >
                <div className="aspect-square bg-black/50">
                  <img loading="lazy" src={CONTROLLER_SPECS[c.key]?.image} alt={c.title} className="w-full h-full object-contain p-3" />
                </div>
                <div className="p-3">
                  <p className="text-white text-sm font-semibold">{c.title}</p>
                  <p className="text-purple-400 text-[11px] uppercase tracking-wider mt-1 group-hover:text-purple-300">{L.viewSheet} →</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MarkingModal = ({ onClose }) => {
  const { lang } = useLang();
  const L = SPEC_LABELS[lang] || SPEC_LABELS.fr;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl bg-[#0a0a0a] border border-white/10 p-0 overflow-hidden rounded-2xl" data-testid="marking-modal">
        <DialogTitle className="sr-only">{L.markingTitle}</DialogTitle>
        <DialogDescription className="sr-only">{L.markingSub}</DialogDescription>
        <button onClick={onClose} aria-label={L.close} className="absolute right-4 top-4 z-10 text-gray-400 hover:text-white transition-colors" data-testid="marking-modal-close">
          <X className="h-5 w-5" />
        </button>

        <div className="p-6">
          <p className="text-purple-400 text-xs uppercase tracking-[0.2em] mb-2">{L.markingSub}</p>
          <h2 className="text-white text-xl font-bold mb-5">{L.markingTitle}</h2>
          <img loading="lazy" src={MARKING_IMAGE} alt={L.markingTitle} className="w-full rounded-xl border border-white/10 bg-black" data-testid="marking-image" />
          <p className="text-gray-400 text-sm leading-relaxed mt-5">{L.markingNote}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
